import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  Palette, Type, Layout, Circle, RotateCcw, X, Sun, Moon, Check,
  Maximize2, Minimize2, Minus
} from 'lucide-react';

/**
 * ThemeSettingsPanel — Full customization flyout  
 * Shows accent color picker (presets + custom hex), font size, layout density, border radius
 */
const ThemeSettingsPanel = ({ isOpen, onClose }) => {
  const {
    prefs, updatePref, resetTheme,
    ACCENT_PRESETS, FONT_SIZES, LAYOUT_DENSITIES, BORDER_RADII,
  } = useTheme();

  const [customHex, setCustomHex] = useState(prefs.accentColor);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  if (!isOpen) return null;

  const isPreset = ACCENT_PRESETS.some(p => p.hex === prefs.accentColor);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-sm z-[60] transition-opacity" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[380px] max-w-[95vw] bg-white dark:bg-gray-900 shadow-2xl z-[61] overflow-y-auto animate-slideInRight">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl" style={{ backgroundColor: prefs.accentColor + '20' }}>
              <Palette className="w-5 h-5" style={{ color: prefs.accentColor }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Appearance</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Customize your experience</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-8">

          {/* ── Section: Mode ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              {prefs.darkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              Mode
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: false, label: 'Light', icon: Sun, desc: 'Clean & bright' },
                { key: true, label: 'Dark', icon: Moon, desc: 'Easy on the eyes' },
              ].map(({ key, label, icon: Icon, desc }) => (
                <button
                  key={String(key)}
                  onClick={() => updatePref('darkMode', key)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    prefs.darkMode === key
                      ? 'border-current shadow-md'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  style={prefs.darkMode === key ? { borderColor: prefs.accentColor } : {}}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`w-5 h-5 ${prefs.darkMode === key ? '' : 'text-gray-400'}`}
                      style={prefs.darkMode === key ? { color: prefs.accentColor } : {}} />
                    {prefs.darkMode === key && <Check className="w-4 h-4" style={{ color: prefs.accentColor }} />}
                  </div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-white">{label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* ── Section: Accent Color ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Palette className="w-4 h-4" /> Accent Color
            </h3>
            <div className="grid grid-cols-5 gap-2.5 mb-3">
              {ACCENT_PRESETS.map((preset) => (
                <button
                  key={preset.hex}
                  onClick={() => { updatePref('accentColor', preset.hex); setCustomHex(preset.hex); setShowCustomPicker(false); }}
                  className="group relative flex flex-col items-center gap-1.5"
                  title={preset.name}
                >
                  <div
                    className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${
                      prefs.accentColor === preset.hex ? 'scale-110 shadow-lg' : 'border-transparent hover:scale-105'
                    }`}
                    style={{
                      backgroundColor: preset.hex,
                      borderColor: prefs.accentColor === preset.hex ? preset.ring : 'transparent',
                    }}
                  >
                    {prefs.accentColor === preset.hex && <Check className="w-4 h-4 text-white drop-shadow" />}
                  </div>
                  <span className="text-[9px] font-medium text-gray-500 dark:text-gray-400">{preset.name}</span>
                </button>
              ))}
            </div>
            {/* Custom hex */}
            <button
              onClick={() => setShowCustomPicker(!showCustomPicker)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${
                !isPreset || showCustomPicker
                  ? 'text-white'
                  : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
              style={!isPreset || showCustomPicker ? { backgroundColor: prefs.accentColor } : {}}
            >
              {showCustomPicker ? 'Custom Color Active' : 'Custom HEX'}
            </button>
            {showCustomPicker && (
              <div className="mt-3 flex items-center gap-3">
                <input
                  type="color"
                  value={customHex}
                  onChange={(e) => { setCustomHex(e.target.value); updatePref('accentColor', e.target.value); }}
                  className="w-12 h-10 rounded-lg cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={customHex}
                  onChange={(e) => {
                    setCustomHex(e.target.value);
                    if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) updatePref('accentColor', e.target.value);
                  }}
                  placeholder="#6366f1"
                  maxLength={7}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm font-mono text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': prefs.accentColor }}
                />
              </div>
            )}
          </div>

          {/* ── Section: Font Size ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Type className="w-4 h-4" /> Font Size
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {FONT_SIZES.map(({ key, label, basePx }) => (
                <button
                  key={key}
                  onClick={() => updatePref('fontSize', key)}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    prefs.fontSize === key
                      ? 'shadow-md'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  style={prefs.fontSize === key ? { borderColor: prefs.accentColor } : {}}
                >
                  <span className="block font-bold text-gray-800 dark:text-white" style={{ fontSize: `${basePx}px` }}>Aa</span>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 block">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Section: Layout Density ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Layout className="w-4 h-4" /> Layout Density
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {LAYOUT_DENSITIES.map(({ key, label }) => {
                const Icon = key === 'compact' ? Minimize2 : key === 'comfortable' ? Maximize2 : Minus;
                return (
                  <button
                    key={key}
                    onClick={() => updatePref('layoutDensity', key)}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      prefs.layoutDensity === key
                        ? 'shadow-md'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    style={prefs.layoutDensity === key ? { borderColor: prefs.accentColor } : {}}
                  >
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${prefs.layoutDensity === key ? '' : 'text-gray-400'}`}
                      style={prefs.layoutDensity === key ? { color: prefs.accentColor } : {}} />
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Section: Border Radius ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Circle className="w-4 h-4" /> Corner Style
            </h3>
            <div className="flex gap-2">
              {BORDER_RADII.map(({ key, label, px }) => (
                <button
                  key={key}
                  onClick={() => updatePref('borderRadius', key)}
                  className={`flex-1 p-3 border-2 transition-all text-center ${
                    prefs.borderRadius === key ? 'shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                  style={{
                    borderRadius: `${px}px`,
                    ...(prefs.borderRadius === key ? { borderColor: prefs.accentColor } : {}),
                  }}
                >
                  <div
                    className="w-6 h-6 mx-auto mb-1"
                    style={{
                      borderRadius: `${px}px`,
                      backgroundColor: prefs.borderRadius === key ? prefs.accentColor : '#d1d5db',
                    }}
                  />
                  <span className="text-[9px] text-gray-500 dark:text-gray-400">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Live Preview Card ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Live Preview</h3>
            <div
              className="p-4 border-2 border-gray-200 dark:border-gray-700"
              style={{ borderRadius: `var(--radius-lg)` }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: prefs.accentColor }}>
                  OP
                </div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white" style={{ fontSize: 'var(--base-font-size)' }}>
                    Sample Card
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">This is how your theme looks</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="px-4 py-2 text-white text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: prefs.accentColor,
                    borderRadius: `var(--radius-base)`,
                  }}
                >
                  Primary Button
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium border"
                  style={{
                    borderRadius: `var(--radius-base)`,
                    color: prefs.accentColor,
                    borderColor: prefs.accentColor + '40',
                    backgroundColor: prefs.accentColor + '10',
                  }}
                >
                  Secondary
                </button>
              </div>
            </div>
          </div>

          {/* ── Reset ── */}
          <button
            onClick={resetTheme}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </>
  );
};

export default ThemeSettingsPanel;
